import {
  DraftGroup,
  DraftNode,
  draftAssociation,
  draftCondition,
  draftGroup,
  draftOpaque,
  isConditionComplete,
  liftNegations
} from './draft';
import { FilterExpr, FilterPredicate, edgePath, makeOp, opArgument, opName } from './wire';

export interface DecodeOptions {
  /** `edge` is the edge path of the association the predicate sits in, `null` at the root. */
  acceptPredicate?(predicate: FilterPredicate, edge: string | null): boolean;
  /** `path` is `edge` at the root, `outer/edge` inside another association. */
  acceptAssociation?(path: string): boolean;
}

export function fromWire(
  expr: FilterExpr | null | undefined,
  options: DecodeOptions = {}
): DraftGroup {
  if (!expr) return draftGroup();
  const node = decode(expr, null, false, true, options);
  return liftNegations(node.kind === 'group' ? node : draftGroup('and', [node]));
}

/**
 * Whatever toWire would drop is kept opaque, so a stored tree never comes back wider than it went
 * in. `scopeRoot`: the whole filter or an association's sub-filter, where an empty `and` means "no
 * condition" and decodes to an empty group.
 */
function decode(
  expr: FilterExpr,
  edge: string | null,
  negated: boolean,
  scopeRoot: boolean,
  options: DecodeOptions
): DraftNode {
  if ('not' in expr) return decode(expr.not, edge, !negated, scopeRoot, options);
  if ('and' in expr || 'or' in expr) {
    const connector = 'and' in expr ? 'and' : 'or';
    const items = 'and' in expr ? expr.and : expr.or;
    // toWire drops empty groups: below the scope root that changes the parent (`any(all(), x)` is
    // every row, not x), and an empty group matching nothing (`any()`, `not all()`) would match
    // every row.
    if (items.length === 0 && (!scopeRoot || (connector === 'or') !== negated)) {
      return { ...draftOpaque(expr), negated };
    }
    const children = items.map((item) => decode(item, edge, false, false, options));
    return { ...draftGroup(connector, children), negated };
  }
  if ('predicate' in expr) {
    const { path, op } = expr.predicate;
    const condition = draftCondition(path, opName(op), opArgument(op));
    // toWire drops an incomplete condition, yet the same argument (`in []`) is a valid stored
    // predicate.
    if (
      !isConditionComplete(condition) ||
      (options.acceptPredicate && !options.acceptPredicate(expr.predicate, edge))
    ) {
      return { ...draftOpaque(expr), negated };
    }
    return { ...condition, negated };
  }
  // A node kind the data layer added after this build is kept as written.
  if (!('association' in expr)) return { ...draftOpaque(expr), negated };
  const association = expr.association;
  const path = edgePath(edge, association.edge);
  // Without a vocabulary to say which nestings exist, only a root association is editable.
  const accepted = options.acceptAssociation
    ? options.acceptAssociation(path)
    : edge === null;
  if (!accepted) return { ...draftOpaque(expr), negated };
  const inner = decode(association.filter, path, false, true, options);
  return {
    ...draftAssociation(association.edge, association.quantifier),
    negated,
    filter: inner.kind === 'group' ? inner : draftGroup('and', [inner])
  };
}

export function toWire(root: DraftGroup): FilterExpr | undefined {
  return encode(root);
}

/** Whether toWire sends the node; an unfinished one is dropped without a trace. */
export function isApplied(node: DraftNode): boolean {
  return encode(node) !== undefined;
}

function encode(node: DraftNode): FilterExpr | undefined {
  const body = encodeBody(node);
  if (body === undefined) return undefined;
  return node.negated ? { not: body } : body;
}

function encodeBody(node: DraftNode): FilterExpr | undefined {
  switch (node.kind) {
    case 'condition':
      if (!isConditionComplete(node)) return undefined;
      return { predicate: { path: node.path!, op: makeOp(node.op!, node.arg) } };
    case 'opaque':
      return node.expr;
    case 'association': {
      const filter = encode(node.filter);
      // A block whose conditions are all unfinished goes like an unfinished condition. One with no
      // condition at all means "has at least one related row", so it is kept as `and: []`.
      if (filter === undefined && node.filter.children.length > 0) return undefined;
      return {
        association: {
          edge: node.edge,
          quantifier: node.quantifier,
          filter: filter ?? { and: [] }
        }
      };
    }
    case 'group': {
      const items = node.children
        .map(encode)
        .filter((item): item is FilterExpr => item !== undefined);
      if (items.length === 0) return undefined;
      if (items.length === 1) return items[0];
      return node.connector === 'and' ? { and: items } : { or: items };
    }
  }
}
